import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';

const API_BASE_URL = process.env.REACT_APP_API_URL || process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';

// Helper: resolve fetch URL for reviews based on entity type
const getReviewsFetchUrl = (apiEndpoint, entityType, entityId) => {
    if (entityType === 'doctor') {
        return `${API_BASE_URL}/doctor-reviews/doctor/${entityId}`;
    }
    if (entityType === 'hospital') {
        return `${API_BASE_URL}/hospital-reviews/hospital/${entityId}`;
    }
    return `${API_BASE_URL}${apiEndpoint}/${entityId}/reviews`;
};

// Helper: resolve submit URL and method for reviews based on entity type
const getReviewsSubmitConfig = (apiEndpoint, entityType, entityId, isUpdate) => {
    if (entityType === 'doctor') {
        return {
            method: isUpdate ? 'put' : 'post',
            url: isUpdate ? `${API_BASE_URL}/doctor-reviews/${entityId}` : `${API_BASE_URL}/doctor-reviews/create`
        };
    }
    if (entityType === 'hospital') {
        return {
            method: isUpdate ? 'put' : 'post',
            url: isUpdate ? `${API_BASE_URL}/hospital-reviews/${entityId}` : `${API_BASE_URL}/hospital-reviews/create`
        };
    }
    // Generic entities (yatras, hotels, cabs, holiday-packages, etc.)
    return {
        method: isUpdate ? 'put' : 'post',
        url: `${API_BASE_URL}${apiEndpoint}/${entityId}/reviews`
    };
};

const getReviewerIdentity = (user) => {
    if (!user) return null;

    const accountType = user.accountType || (user.isVisitor ? 'visitor' : 'vendor');
    const reviewerId = accountType === 'visitor'
        ? (user.visitor_id || user.visitorId || user.user_id || user.id)
        : (user.vendor_id || user.vendorId || user.id);

    return {
        id: reviewerId,
        type: accountType,
        isVendor: accountType === 'vendor',
        isVisitor: accountType === 'visitor'
    };
};

const reviewBelongsToReviewer = (review, reviewer) => {
    if (!review || !reviewer?.id) return false;

    const reviewUserId = review.user_id || review.userId || review.User?.id || review.user?.id;
    const reviewVendorId = review.vendor_id || review.vendorId || review.Vendor?.id;
    const reviewVisitorId = review.visitor_id || review.visitorId || review.Visitor?.id;

    if (reviewer.type === 'vendor') {
        return String(reviewVendorId || reviewUserId) === String(reviewer.id);
    }

    return String(reviewVisitorId || reviewUserId) === String(reviewer.id);
};

/**
 * Reusable Review Modal Component (Holiday Listing Style)
 */
const ReviewModal = ({ isOpen, onClose, entity, entityType, user, apiEndpoint, onReviewSubmitted, onLoginRequired }) => {
    const [reviews, setReviews] = useState([]);
    const [myReview, setMyReview] = useState(null);
    const [showWriteForm, setShowWriteForm] = useState(false);
    const [sortOption, setSortOption] = useState('most_relevant');
    const [reviewRating, setReviewRating] = useState(5);
    const [reviewText, setReviewText] = useState("");
    const [isReviewSubmitting, setIsReviewSubmitting] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    // Fetch reviews on mount or when entity changes
    useEffect(() => {
        if (isOpen && entity) {
            fetchReviews();
        }
    }, [isOpen, entity]);

    const fetchReviews = async () => {
        setIsLoading(true);
        try {
            const fetchUrl = getReviewsFetchUrl(apiEndpoint, entityType, entity.id);
            const res = await axios.get(fetchUrl);
            if (res.data && (res.data.success || res.data.status || res.data.reviews || res.data.data)) {
                const allReviews = res.data.data || res.data.reviews || [];
                setReviews(allReviews);
                const reviewer = getReviewerIdentity(user);
                if (reviewer) {
                    const found = allReviews.find(r => reviewBelongsToReviewer(r, reviewer));
                    if (found) {
                        setMyReview(found);
                        setReviewRating(found.rating);
                        setReviewText(found.review_text);
                    } else {
                        setMyReview(null);
                        setReviewRating(5);
                        setReviewText("");
                    }
                }
            }
        } catch (err) {
            console.error('Failed to fetch reviews:', err);
            toast.error('Failed to load reviews');
        } finally {
            setIsLoading(false);
        }
    };

    const handleWriteClick = () => {
        if (!user) {
            if (onLoginRequired) {
                onLoginRequired();
            } else {
                toast.error('Please login to write a review');
            }
            return;
        }
        setShowWriteForm(!showWriteForm);
    };

    const handleReviewSubmit = async (e) => {
        e.preventDefault();
        if (!user) {
            if (onLoginRequired) {
                onLoginRequired();
            } else {
                toast.error('Please login to submit a review');
            }
            return;
        }

        if (!reviewText.trim()) {
            toast.error('Please write a review');
            return;
        }

        const reviewer = getReviewerIdentity(user);
        if (!reviewer?.id) {
            toast.error('Unable to identify the logged-in user. Please login again.');
            return;
        }

        setIsReviewSubmitting(true);
        const keyName = entityType === 'holiday_package' ? 'package_id' : `${entityType}_id`;
        const payload = {
            rating: reviewRating,
            review_text: reviewText,
            [keyName]: entity.id
        };

        try {
            const token = localStorage.getItem('authToken') || localStorage.getItem('token');
            const headers = token ? { Authorization: `Bearer ${token}` } : {};
            const { method, url } = getReviewsSubmitConfig(apiEndpoint, entityType, entity.id, !!myReview);

            const res = await axios[method](
                url,
                payload,
                { headers }
            );

            if (res.data && (res.data.success || res.data.status || res.data.reviews || res.data.data || res.data.message)) {
                toast.success(myReview ? "Review Updated!" : "Review Added!");

                // Optimistic update: immediately add/update review in the UI
                if (myReview) {
                    setReviews(reviews.map(r =>
                        reviewBelongsToReviewer(r, reviewer)
                            ? { ...r, rating: reviewRating, review_text: reviewText }
                            : r
                    ));
                    setMyReview({ ...myReview, rating: reviewRating, review_text: reviewText });
                } else {
                    const newReview = {
                        id: res.data.data?.review?.id || Math.random(),
                        user_id: reviewer.id,
                        reviewer_type: reviewer.type,
                        user_name: user.name || user.username || user.phone,
                        rating: reviewRating,
                        review_text: reviewText,
                        created_at: new Date().toISOString()
                    };
                    setReviews([newReview, ...reviews]);
                    setMyReview(newReview);
                }

                // Reset form
                setShowWriteForm(false);
                setReviewRating(5);
                setReviewText("");

                if (onReviewSubmitted) {
                    onReviewSubmitted();
                }

                // Refresh from server to ensure data consistency
                setTimeout(async () => {
                    await fetchReviews();
                }, 1000);
            }
        } catch (err) {
            toast.error(err.response?.data?.message || "Error processing review.");
            console.error('Review submission error:', err);
        } finally {
            setIsReviewSubmitting(false);
        }
    };

    if (!isOpen) return null;

    const avgRating = reviews.length > 0
        ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1)
        : '0.0';

    const entityName = entity?.doctor_name || entity?.hospital_name || entity?.package_name || entity?.yatra_name || entity?.name || entity?.hotel_name || entity?.brand_name || 'Product';

    const locationText = entityType === 'doctor' || entityType === 'hospital'
        ? [entity?.address, entity?.city, entity?.state, entity?.country, entity?.pincode].filter(Boolean).join(', ')
        : '';

    return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 99999, backdropFilter: 'blur(2px)' }} onClick={onClose}>
            <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', padding: 0, borderRadius: 8, width: 920, maxWidth: '96%', maxHeight: '88vh', overflowY: 'auto', position: 'relative', zIndex: 100000, boxShadow: '0 8px 30px rgba(0,0,0,0.3)' }}>

                {/* Header */}
                <div className="review-custom-header" style={{ padding: '18px 22px 14px', borderBottom: '1px solid #eee', position: 'relative' }}>
                    <div>
                        <h2 style={{ margin: 0, fontSize: 22, paddingRight: 35 }}>{entityName}</h2>
                        <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 10 }}>
                            <strong style={{ fontSize: 20 }}>{avgRating}</strong>
                            <span>
                                {[1, 2, 3, 4, 5].map(i => {
                                    const parsedAvg = parseFloat(avgRating);
                                    if (i <= Math.floor(parsedAvg)) return <i key={i} className="bi bi-star-fill" style={{ color: '#f4b400', fontSize: 18, marginRight: 4 }} />;
                                    if (i === Math.floor(parsedAvg) + 1 && parsedAvg - Math.floor(parsedAvg) >= 0.5) return <i key={i} className="bi bi-star-half" style={{ color: '#f4b400', fontSize: 18, marginRight: 4 }} />;
                                    return <i key={i} className="bi bi-star" style={{ color: '#f4b400', fontSize: 18, marginRight: 4, opacity: 0.5 }} />;
                                })}
                            </span>
                            <span style={{ color: '#666' }}>({reviews.length} {reviews.length === 1 ? 'review' : 'reviews'})</span>
                        </div>
                        {locationText && (
                            <div style={{ color: '#666', marginTop: 8, fontSize: 14 }}>
                                <i className="bi bi-geo-alt-fill text-danger me-1" /> {locationText}
                            </div>
                        )}
                    </div>
                    <div className="review-header-actions" style={{ display: 'flex', gap: 8 }}>
                        <button onClick={handleWriteClick} style={{ padding: "8px 14px", borderRadius: "20px", border: "1px solid rgb(0, 123, 255)", background: "#fff", color: "rgb(0, 123, 255)", cursor: "pointer", whiteSpace: "nowrap" }}>
                            {myReview ? 'Edit your review' : 'Write a review'}
                        </button>
                    </div>
                    <button onClick={onClose} style={{ position: 'absolute', top: 18, right: 22, background: 'transparent', border: 'none', fontSize: 28, cursor: 'pointer', lineHeight: 1 }}>&times;</button>
                </div>

                {/* Sort Section */}
                <div style={{ padding: '12px 22px', borderBottom: '1px solid #f7f7f7' }}>
                    <div style={{ marginBottom: 10, color: '#333' }}>Sort by</div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        {['most_relevant', 'newest', 'highest', 'lowest'].map((opt) => {
                            const label = opt === 'most_relevant' ? 'Most relevant' : opt === 'newest' ? 'Newest' : opt === 'highest' ? 'Highest rating' : 'Lowest rating';
                            return <button key={opt} onClick={() => setSortOption(opt)} className={`btn ${sortOption === opt ? 'btn-primary' : 'btn-outline-secondary'}`} style={{ borderRadius: 20, padding: '6px 12px', cursor: 'pointer' }}>{label}</button>;
                        })}
                    </div>
                </div>

                {/* Write/Edit Form */}
                {showWriteForm && (
                    <form onSubmit={handleReviewSubmit} style={{ padding: '20px 22px', borderBottom: '1px solid #f7f7f7' }}>
                        <div style={{ textAlign: 'center', marginBottom: 12 }}>
                            <label style={{ display: 'block', marginBottom: 6, fontWeight: 500 }}>Your Rating:</label>
                            <div style={{ display: 'flex', justifyContent: 'center', gap: 8 }}>
                                {[1, 2, 3, 4, 5].map((r) => (
                                    <i
                                        key={r}
                                        className={r <= reviewRating ? 'bi bi-star-fill' : 'bi bi-star'}
                                        style={{ color: '#f4b400', fontSize: 30, cursor: 'pointer', transform: r <= reviewRating ? 'scale(1.15)' : 'scale(1)', transition: 'transform 0.12s' }}
                                        onClick={() => setReviewRating(r)}
                                    />
                                ))}
                            </div>
                        </div>
                        <textarea value={reviewText} onChange={(e) => setReviewText(e.target.value)} placeholder="Write your review..." rows={4} style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid #ddd' }} required />
                        <div style={{ marginTop: 14, display: 'flex', justifyContent: 'center', gap: 12 }}>
                            <button type="submit" className="btn btn-primary" style={{ minWidth: 120 }} disabled={isReviewSubmitting}>
                                {isReviewSubmitting ? "Saving..." : (myReview ? 'Update' : 'Post')}
                            </button>
                            <button type="button" className="btn btn-secondary" style={{ minWidth: 120 }} onClick={() => setShowWriteForm(false)}>Cancel</button>
                        </div>
                    </form>
                )}

                {/* Reviews List */}
                <div style={{ padding: '12px 22px 22px 22px' }}>
                    {isLoading ? (
                        <div style={{ textAlign: 'center', padding: '18px 0' }}>
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        </div>
                    ) : reviews.length === 0 ? (
                        <div style={{ color: '#666', padding: '18px 0' }}>No reviews yet. Be the first to review!</div>
                    ) : (() => {
                        const sorted = [...reviews];
                        if (sortOption === 'newest') sorted.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
                        else if (sortOption === 'highest') sorted.sort((a, b) => (b.rating || 0) - (a.rating || 0));
                        else if (sortOption === 'lowest') sorted.sort((a, b) => (a.rating || 0) - (b.rating || 0));

                        return sorted.map((r, index) => {
                            const displayName = r.user_name || r.User?.name || r.User?.phone || `User #${r.user_id}`;
                            const avatarLetter = displayName ? displayName.charAt(0).toUpperCase() : 'G';
                            const createdAt = new Date(r.created_at);
                            const formattedDate = isNaN(createdAt.getTime()) ? '' : createdAt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });

                            return (
                                <div key={index} style={{ borderBottom: '1px solid #eee', paddingBottom: 12, marginBottom: 12 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                        <div style={{ width: 42, height: 42, borderRadius: '50%', background: '#f1f1f1', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 600 }}>
                                            {avatarLetter}
                                        </div>
                                        <div>
                                            <div style={{ fontWeight: 600 }}>{displayName}</div>
                                            <div style={{ fontSize: 12, color: '#666' }}>{formattedDate}</div>
                                        </div>
                                        <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
                                            <div>
                                                {[1, 2, 3, 4, 5].map(i => {
                                                    if (i <= r.rating) return <i key={i} className="bi bi-star-fill" style={{ color: '#f4b400', fontSize: 16, marginRight: 4 }} />;
                                                    return <i key={i} className="bi bi-star" style={{ color: '#f4b400', fontSize: 16, marginRight: 4, opacity: 0.5 }} />;
                                                })}
                                            </div>
                                            <div style={{ fontSize: 12, color: '#666' }}>{(r.rating || 0).toFixed(1)} ★</div>
                                        </div>
                                    </div>
                                    {r.review_text && <div style={{ marginTop: 8 }}>{r.review_text}</div>}
                                </div>
                            );
                        });
                    })()}
                </div>
            </div>

            <style>{`
                .review-custom-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                }
                @media (max-width: 576px) {
                    .review-custom-header {
                        display: block !important;
                    }
                    .review-header-actions {
                        margin-top: 12px;
                    }
                }
            `}</style>
        </div>
    );
};

export default ReviewModal;
